import React, { FC } from 'react'
import { Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { EditorSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

import { useAttachedCompanies, useAttachedInstallers } from 'src/Libs/retrofit'
import { Installation } from 'src/Libs/installations/models/installation.model'
import { isFlowTheme } from 'src/Libs/utils'
import CompaniesWithAccess from './companies-with-access/CompaniesWithAccessWrapper'
import { InstallersWithAccess } from './installers-with-access'
import InstallationInformationTable from './InstallationInformationTable'
import OwnerAccountLinkData from './OwnerAccountLinkData'
import PaperContainer from './PaperContainer'

interface InformationProps {
  installation: Installation | null | undefined
  installationLoading: boolean
  installationId: string | undefined
  refetchInstallation: () => void
}

export const useInstallationDetailsStyles = makeStyles()(() => ({
  paperContent: {
    paddingInline: 0,
  },
}))

/**
 * This component represents the information part for the installation details page
 * @returns Information Component
 **/
const Information: FC<InformationProps> = ({ installation, refetchInstallation, installationLoading, installationId }) => {
  const { t } = useCustomTranslation()
  const { classes } = useInstallationDetailsStyles()
  const attachedCompaniesHookResult = useAttachedCompanies(installationId, !isFlowTheme)
  const attachedInstallersHookResult = useAttachedInstallers(installationId, isFlowTheme)

  const refetchInstallationWithAttachedCompanies = (): void => {
    refetchInstallation()
    if (isFlowTheme) attachedInstallersHookResult.refetch()
    else attachedCompaniesHookResult.refetch()
  }

  const isInstallationDecommissioned = !!installation?.decommissionedAt

  if (installationLoading) {
    return (
      <Box>
        <EditorSkeleton />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PaperContainer title={t('installation.details.info.title')} contentClassName={classes.paperContent}>
        <InstallationInformationTable
          installation={installation}
          installationId={installationId}
          isInstallationDecommissioned={isInstallationDecommissioned}
          refetchInstallation={refetchInstallation}
        />
      </PaperContainer>
      {installation && (
        <PaperContainer title={t('installation.details.info.owner-information-title')} contentClassName={classes.paperContent}>
          <OwnerAccountLinkData
            installation={installation}
            installationId={installationId}
            isInstallationDecommissioned={isInstallationDecommissioned}
            refetchInstallationWithAttachedCompanies={refetchInstallationWithAttachedCompanies}
          />
        </PaperContainer>
      )}
      {isFlowTheme ? (
        <PaperContainer title={t('installation.details.info.installers-with-access')} contentClassName={classes.paperContent}>
          <InstallersWithAccess
            installationId={installationId}
            isInstallationDecommissioned={isInstallationDecommissioned}
            attachedInstallersHookResult={attachedInstallersHookResult}
            refetchData={refetchInstallationWithAttachedCompanies}
          />
        </PaperContainer>
      ) : (
        <PaperContainer title={t('installation.details.info.companies-with-access')} contentClassName={classes.paperContent}>
          <CompaniesWithAccess
            installationId={installationId}
            attachedCompaniesHookResult={attachedCompaniesHookResult}
            refetchData={refetchInstallationWithAttachedCompanies}
            isInstallationDecommissioned={isInstallationDecommissioned}
          />
        </PaperContainer>
      )}
    </Box>
  )
}

export default Information
